import {
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { auth } from "../../../core/auth";
import useApi from "../../../shared/hooks/useApi";
import { OfficeId, PlanOfCareTypeId } from "../../../shared/schema/schema";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";
import {
  buildOfficeRows,
  emitRefreshPlansOfCare,
  OfficePlanOfCareRow,
} from "../plan-of-care-type.utils";
import PlanOfCareTypeCreateModal from "./PlanOfCareTypeCreateModal";
import PlanOfCareTypeItemsModal from "./PlanOfCareTypeItemsModal";

type ModalState =
  | { type: "closed" }
  | { type: "create" }
  | {
      type: "edit";
      officeName: string;
      planOfCareTypeId: PlanOfCareTypeId;
      showPatientSignature: boolean;
    };

export default function PlanOfCareTypeSettingsSection() {
  const { queries } = useApi();
  const [modalState, setModalState] = React.useState<ModalState>({ type: "closed" });

  const [officesQuery, settingsQuery] = useQueries({
    queries: [queries.office.list(), queries.planOfCareType.settings()],
  });

  const rows = React.useMemo(
    () =>
      buildOfficeRows({
        offices: officesQuery.data?.offices ?? [],
        settings: settingsQuery.data?.planOfCares ?? [],
      }),
    [officesQuery.data, settingsQuery.data]
  );

  const officesWithoutPlanOfCare = rows.filter((row) => row.planOfCareTypeId === null);
  const isLoading = officesQuery.isLoading || settingsQuery.isLoading;

  const getShowPatientSignature = (officeId: OfficeId) =>
    settingsQuery.data?.planOfCares.find((poc) => poc.officeId === officeId)
      ?.showPatientSignature ?? false;

  return (
    <>
      <Flex align="center" justify="space-between" mb={4}>
        <Heading size="md">Plans Of Care</Heading>
        {officesWithoutPlanOfCare.length > 0 && (
          <Button colorScheme="blue" onClick={() => setModalState({ type: "create" })}>
            Add new POC configuration
          </Button>
        )}
      </Flex>

      <Box borderColor="gray.200" borderRadius="md" borderWidth={1} overflowX="auto">
        <Table>
          <Thead bg="gray.50">
            <Tr>
              <Th>Office</Th>
              <Th isNumeric={true}>Tasks</Th>
              <Th width="160px">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row) => (
              <OfficeRow
                key={row.officeId}
                row={row}
                onClickEdit={() =>
                  row.planOfCareTypeId !== null &&
                  setModalState({
                    type: "edit",
                    officeName: row.officeName,
                    planOfCareTypeId: row.planOfCareTypeId,
                    showPatientSignature: getShowPatientSignature(row.officeId),
                  })
                }
              />
            ))}
            {rows.length === 0 && (
              <Tr>
                <Td colSpan={3}>
                  <Text color="gray.500" py={6} textAlign="center">
                    {isLoading ? "Loading offices..." : "No offices"}
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {modalState.type === "create" && (
        <PlanOfCareTypeCreateModal
          offices={officesWithoutPlanOfCare.map((row) => ({
            id: row.officeId,
            name: row.officeName,
          }))}
          onClose={() => setModalState({ type: "closed" })}
        />
      )}
      {modalState.type === "edit" && (
        <PlanOfCareTypeItemsModal
          key={modalState.planOfCareTypeId}
          officeName={modalState.officeName}
          planOfCareTypeId={modalState.planOfCareTypeId}
          showPatientSignature={modalState.showPatientSignature}
          onClose={() => setModalState({ type: "closed" })}
        />
      )}
    </>
  );
}

function OfficeRow(props: { row: OfficePlanOfCareRow; onClickEdit: () => void }) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  const copyFromTemplateMutation = useMutation({
    ...mutations.planOfCareType.copyFromTemplate(props.row.officeId),
    onError: (error) =>
      toast({
        title: "Could not copy plan of care document",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queries.planOfCareType.settings().queryKey });
      emitRefreshPlansOfCare();
      toast({
        title: "Plan of care for office created successfully",
        status: "success",
        position: "top-right",
      });
    },
  });

  return (
    <Tr>
      <Td>{props.row.officeName}</Td>
      <Td isNumeric={true}>{props.row.planOfCareTypeId === null ? "-" : props.row.itemCount}</Td>
      <Td>
        {props.row.planOfCareTypeId === null ? (
          <Button
            isLoading={copyFromTemplateMutation.isPending}
            size="sm"
            variant="outline"
            onClick={() => copyFromTemplateMutation.mutate()}
          >
            Create from template
          </Button>
        ) : (
          <Button colorScheme="blue" size="sm" onClick={props.onClickEdit}>
            {auth.isAllowedTo("edit_plan_of_care_items") ? "Edit" : "View"}
          </Button>
        )}
      </Td>
    </Tr>
  );
}
